//
//  src/layouts/default/contact/Contact.js
//
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import Nav    from "../../../components/nav/Nav";
import Footer from "../../../components/footer/Footer";
import Wizard from "./wizard/Wizard";
import "./Contact.css";

const IconPen = () => (
    <svg viewBox="0 0 100 100" className="opt-svg pen-paper" aria-hidden="true">
        <rect x="15" y="20" width="70" height="60" rx="2" stroke="currentColor" strokeWidth="3" fill="none" />
        <line x1="25" y1="35" x2="65" y2="35" stroke="currentColor" strokeWidth="2" />
        <line x1="25" y1="45" x2="55" y2="45" stroke="currentColor" strokeWidth="2" />
        <line x1="25" y1="55" x2="70" y2="55" stroke="currentColor" strokeWidth="2" />
        <path d="M70 15 L85 5 L85 20 Z" fill="currentColor" className="pen" />
    </svg>
);

const IconInk = () => (
    <svg viewBox="0 0 100 100" className="opt-svg ink-drop" aria-hidden="true">
        <path d="M 50 12 C 50 12, 28 42, 28 62 C 28 76, 38 86, 50 86 C 62 86, 72 76, 72 62 C 72 42, 50 12, 50 12 Z"
              stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path d="M 42 56 C 42 50, 46 46, 50 46" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
);

export default function Contact() {
    const navigate = useNavigate();
    const location = useLocation();

    const isWizardPath = /^\/contact\/wizard/.test(location.pathname);
    const [transitioning, setTransitioning] = React.useState(false);

    const canonicalUrl  = `https://jefftattooer.com${location.pathname}`.replace(/\/$/, "") || "https://jefftattooer.com/contact";
    const ogImage       = "https://jefftattooer.com/logo512.png";
    const pageTitle     = "Contact Jeff Tattooer | Book a Tattoo Consultation in Melbourne";
    const pageDescription = "Get in touch with Jeff Tattooer. Send a message or walk through a quick enquiry to plan your next tattoo. Melbourne-based tattoo studio.";

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": pageTitle,
        "description": pageDescription,
        "url": canonicalUrl,
        "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home",    "item": "https://jefftattooer.com" },
                { "@type": "ListItem", "position": 2, "name": "Contact", "item": canonicalUrl },
            ],
        },
    };

    const goTo = (path, dir = "forward") => {
        setTransitioning(true);
        setTimeout(() => {
            navigate(path);
            window.scrollTo({ top: 0, behavior: "instant" });
        }, 280);
    };

    const handleIntroChoice = (choice) => {
        if (choice === "message") {
            goTo("/contact/form/");
            return;
        }
        goTo("/contact/wizard/type/");
    };

    React.useEffect(() => {
        if (!isWizardPath) {
            setTransitioning(false);
        }
    }, [isWizardPath]);

    return (
        <div id="contact">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title"       content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image"       content={ogImage} />
                <meta property="og:url"         content={canonicalUrl} />
                <meta property="og:type"        content="website" />
                <meta property="og:site_name"   content="Jeff Tattooer" />
                <meta name="twitter:card"        content="summary_large_image" />
                <meta name="twitter:title"       content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image"       content={ogImage} />
            </Helmet>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            <Nav />

            <div className="contact-choice-container">
                {isWizardPath ? (
                    <Wizard />
                ) : (
                    <div className={`contact-screen ${transitioning ? "leaving" : "entering"}`}>
                        <div className="contact-choice-header">
                            <h1 className="contact-choice-title">Get in Touch</h1>
                            <p className="contact-choice-subtitle">
                                Got a question, an idea, or ready to plan your next piece?
                                <br />Pick the path that suits you or send a message. No question is too small.
                            </p>
                        </div>

                        <div className="contact-choice-options two-up">
                            <button
                                type="button"
                                className="contact-option booking-option"
                                onClick={() => handleIntroChoice("tattoo")}
                            >
                                <div className="option-content">
                                    <div className="option-icon booking-icon"><IconInk /></div>
                                    <h2 className="option-title">Enquire About a Tattoo</h2>
                                    <p className="option-description">A few quick questions to plan your next piece.</p>
                                    <div className="option-badge preferred">Free Consultation</div>
                                </div>
                                <div className="ink-drip drip-1"></div>
                                <div className="ink-drip drip-2"></div>
                                <div className="ink-drip drip-3"></div>
                            </button>

                            <button
                                type="button"
                                className="contact-option message-option"
                                onClick={() => handleIntroChoice("message")}
                            >
                                <div className="option-content">
                                    <div className="option-icon form-icon"><IconPen /></div>
                                    <h2 className="option-title">Send a Message</h2>
                                    <p className="option-description">No question is too small.</p>
                                    <div className="option-badge default-choice">Message Me</div>
                                </div>
                                <div className="ink-drip drip-1"></div>
                                <div className="ink-drip drip-2"></div>
                                <div className="ink-drip drip-3"></div>
                            </button>
                        </div>

                        <div className="contact-reassurance">
                            <div className="reassurance-item">
                                <div className="reassurance-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
                                    </svg>
                                </div>
                                <div className="reassurance-text">
                                    <h3>Personal reply</h3>
                                    <p>Every message lands with me directly. No bots, no call centres.</p>
                                </div>
                            </div>
                            <div className="reassurance-item">
                                <div className="reassurance-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                </div>
                                <div className="reassurance-text">
                                    <h3>Quick to respond</h3>
                                    <p>Most enquiries get a reply within a day or two.</p>
                                </div>
                            </div>
                            <div className="reassurance-item">
                                <div className="reassurance-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z" />
                                    </svg>
                                </div>
                                <div className="reassurance-text">
                                    <h3>No commitment</h3>
                                    <p>Reach out to chat, plan, or just ask. Consultations are always free.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}
